import React from 'react'
import { Table } from 'antd'
import dayjs from 'dayjs'
import {
  getUserIdentifierFromRecord,
  getEventData,
  formatMs,
  formatTs,
  toMs,
  filterUsageEventsByDate,
} from '../utils'
import { useColumnSearch } from './useColumnSearch'

// we only care about these event types in the table
const REL_EVT_TYPES = [
  'MONITOR_PAGE_TIME_SPENT',
  'MONITOR_PAGE_LOADED',
  'MONITOR_PAGE_DOWNLOADED',
]

// human readable labels for the event types
export const EVENT_TYPE_LABELS = {
  MONITOR_PAGE_TIME_SPENT: 'close',
  MONITOR_PAGE_LOADED: 'start',
  MONITOR_PAGE_DOWNLOADED: 'export',
}

export default function UserSessionTable({
  data,
  plantFilter,
  selectedDate,
  selectedUser,
  userIdentifier = 'user_id',
}) {
  const [filtered, setFiltered] = React.useState([])

  // time spent can be in timeOnMonitorTable, timeOnSubsystem,
  // timeOnCircuit, timeOnComponent, or userTimeSpent
  const getRecordTimeSpent = (record) => {
    const ev = getEventData(record)
    return (
      ev.timeOnMonitorTable ||
      ev.timeOnSubsystem ||
      ev.timeOnCircuit ||
      ev.timeOnComponent ||
      ev.userTimeSpent ||
      0
    )
  }

  const dedupeEvents = (events) => {
    // we have events for when they started and ended the session
    // start = MONITOR_PAGE_LOADED, end = MONITOR_PAGE_TIME_SPENT,
    // so we want to combine these into one row per session if possible.
    // We can match them by user and by the loggedAt time
    // (the time spent event should have a loggedAt time that is close to the loaded event)
    const starts = {}
    const ends = {}
    // build these two objects keyed by user+start_time rounded to the nearest second
    events.forEach((ev) => {
      const identifier = getUserIdentifierFromRecord(ev, userIdentifier)
      const plant = ev.eventData?.plant || 'unknown'
      let timeKey = null
      if (ev.eventType === 'MONITOR_PAGE_LOADED') {
        timeKey = dayjs(ev.loggedAt)
          .toISOString()
          .replace(/\.\d{3}/, '')
        starts[`${identifier}-${plant}-${timeKey}`] = ev
      } else if (ev.eventType === 'MONITOR_PAGE_TIME_SPENT') {
        timeKey = dayjs(ev.loggedAt - getRecordTimeSpent(ev))
          .toISOString()
          .replace(/\.\d{3}/, '')
        ends[`${identifier}-${plant}-${timeKey}`] = ev
      }
    })
    // iterate through starts - if we have an end for it, insert
    // that into the final results. If we don't, insert the start.
    const deduped = []
    Object.keys(starts).forEach((key) => {
      if (ends[key]) {
        deduped.push(ends[key])
      } else {
        deduped.push(starts[key])
      }
    })
    // Add any events that aren't start/end types (e.g. MONITOR_PAGE_DOWNLOADED)
    events.forEach((ev) => {
      if (
        ev.eventType !== 'MONITOR_PAGE_LOADED' &&
        ev.eventType !== 'MONITOR_PAGE_TIME_SPENT'
      ) {
        deduped.push(ev)
      }
    })
    return deduped
  }

  const filterEvents = (
    data,
    { selectedUser, plantFilter, selectedDate, userIdentifier },
  ) => {
    let filtered = Array.isArray(data)
      ? data.filter(
          (ev) => ev.eventType && REL_EVT_TYPES.includes(ev.eventType),
        )
      : []
    if (selectedUser) {
      filtered = filtered.filter((row) => {
        const identifier = getUserIdentifierFromRecord(row, userIdentifier)
        return identifier === selectedUser
      })
    }
    if (plantFilter) {
      filtered = filtered.filter(
        (row) => getEventData(row).plant === plantFilter,
      )
    }
    filtered = filterUsageEventsByDate(filtered, selectedDate)
    return dedupeEvents(filtered)
  }

  React.useEffect(() => {
    const filtered = filterEvents(data, {
      selectedUser,
      plantFilter,
      selectedDate,
      userIdentifier,
    })
    setFiltered(filtered)
  }, [data, selectedUser, plantFilter, selectedDate, userIdentifier])

  const { getColumnSearchProps } = useColumnSearch(['user', 'plant'])

  const columns = [
    {
      title: 'User',
      dataIndex: 'user',
      key: 'user',
      ...getColumnSearchProps(
        'user',
        (record) => getUserIdentifierFromRecord(record, userIdentifier),
        'Search user',
      ),
      sorter: (a, b) => {
        const userA = getUserIdentifierFromRecord(a, userIdentifier) || ''
        const userB = getUserIdentifierFromRecord(b, userIdentifier) || ''
        return userA.localeCompare(userB)
      },
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Event',
      dataIndex: 'eventType',
      key: 'eventType',
      render: (ev) => EVENT_TYPE_LABELS[ev] || ev,
      sorter: (a, b) => {
        const evA = a.eventType || ''
        const evB = b.eventType || ''
        return evA.localeCompare(evB)
      },
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Plant',
      dataIndex: 'plant',
      key: 'plant',
      ...getColumnSearchProps(
        'plant',
        (record) => getEventData(record).plant || '',
        'Search plant',
      ),
      sorter: (a, b) =>
        (getEventData(a).plant || '').localeCompare(
          getEventData(b).plant || '',
        ),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Time Spent',
      dataIndex: 'timeSpent',
      key: 'timeSpent',
      render: (val, record) => formatMs(getRecordTimeSpent(record)),
      sorter: (a, b) => {
        return getRecordTimeSpent(a) - getRecordTimeSpent(b)
      },
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Start',
      dataIndex: 'start',
      key: 'start',
      render: (val, record) => {
        if (
          record.eventType === 'MONITOR_PAGE_LOADED' ||
          record.eventType === 'MONITOR_PAGE_DOWNLOADED'
        ) {
          return formatTs(record.loggedAt)
        }
        const endMs = toMs(record.loggedAt)
        if (!endMs) return '-'
        return formatTs(endMs - getRecordTimeSpent(record))
      },
      sorter: (a, b) => {
        const aStart = toMs(a.loggedAt) - getRecordTimeSpent(a)
        const bStart = toMs(b.loggedAt) - getRecordTimeSpent(b)
        return aStart - bStart
      },
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'End',
      dataIndex: 'end',
      key: 'end',
      render: (val, record) => {
        if (
          record.eventType === 'MONITOR_PAGE_LOADED' ||
          record.eventType === 'MONITOR_PAGE_DOWNLOADED'
        ) {
          return '-'
        }
        return formatTs(record.loggedAt)
      },
      sorter: (a, b) => toMs(a.loggedAt) - toMs(b.loggedAt),
      sortDirections: ['ascend', 'descend'],
      defaultSortOrder: 'descend',
    },
  ]

  return (
    <Table
      columns={columns}
      dataSource={filtered}
      rowKey={(row, idx) => row.id || row.timestamp || idx}
      pagination={{ pageSize: 5, showSizeChanger: false }}
      size="small"
    />
  )
}
